import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { Input, InputNumber, Select, Spin, Switch } from 'antd';
import { Tag } from '../../components/tag';
import { useFormik, FormikProps } from 'formik';
import {
    useAuthStore,
    useLoyaltyClubStore,
    usePromotionStore,
    useBusinessUnitStore,
    useGamesStore,
} from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import {
    APP_TARGETS,
    ExternalProviderEnum,
    LoyaltyClubCreatePayload,
    LoyaltyClubUpdatePayload,
    LoyaltyClub,
    LoyaltyClubTierRewardInput,
    LoyaltyProgram,
    YupLoyaltyClubCreatePayload,
    YupLoyaltyClubUpdatePayload,
    APP_TARGET_CONFIG,
} from '../../stores/loyalty-club';
import {
    buildReusableCouponPayload,
    REUSABLE_COUPON_REWARD_TYPE,
    ReusableCouponPromotion,
    ReusableCouponRow,
    reusableCouponRowsFromTierRewards,
    UnlimitedCoupons,
    validateReusableCoupons,
} from './components/UnlimitedCoupons';
import {
    canLinkRelatedGame,
    relatedGameIdForSave,
    REPEATABLE_PURCHASE_GRANT,
    saveClubWithReusableCoupons,
} from './save-club';
import { Lang } from '../../utils';
import { useNavigation } from '../../utils/use-navigation';
import { ImageInput } from '../../components/image-input';
import { LabeledField } from '../../components/form-fields/labeled-field';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { SaveButton } from '../../components/action-buttons';
import { Section } from '../../components/form-fields/section';
import { DatePicker } from '../../components/date-picker';
import { isGameLinkableToLoyaltyClub } from '../../utils/loyalty-club-game-scope';
import {
    getGameAvailabilityStatus,
    GameAvailabilityStatusTag,
} from '../../utils/game-availability-status';
import style from './style.module.scss';

const DATE_FORMAT = 'YYYY-MM-DD';

const PROVIDER_OPTIONS: ExternalProviderEnum[] = ['six12', 'lift'];

/** Dates-only edit of one tier reward, sent on the club update. */
export interface TierSchedule {
    startDate: string | null;
    endDate: string | null;
}

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;

// Formik holds a superset of both payloads: the shared fields plus the
// create-only config fields (program, app target, provider, slug). Provinces
// are derived from the app target on the backend. In edit mode the config
// fields are ignored when building the update payload.
type ClubFormValues = {
    name: string;
    isActive: boolean;
    startDate: string;
    registrationStartDate: string;
    registrationEndDate: string;
    endDate: string;
    translations: {
        language: typeof Lang[number];
        name: string;
        subtitle: string | null;
        description: string | null;
        image: string | null;
    }[];
    loyaltyProgramId: number | null;
    appTarget: string;
    externalProvider: ExternalProviderEnum | null;
    six12PromotionId: string | null;
    externalGameId: number | null;
    slug: string;
    provinces: number[];
    tierReward: TierRewardFormValues;
};

type TierRewardTranslationFormValues = {
    language: typeof Lang[number];
    title: string;
    description: string;
    image: string;
    pushNotificationTitle: string;
    pushNotificationBody: string;
    completedTitle: string;
    completedBody: string;
};

type TierRewardFormValues = {
    promotionId: number | null;
    requiredPurchases: number | null;
    grantValidityDays: number | null;
    maxClaims: number | null;
    translations: TierRewardTranslationFormValues[];
};

function emptyTierRewardTranslations(): TierRewardTranslationFormValues[] {
    return Lang.map((language) => ({
        language,
        title: '',
        description: '',
        image: '',
        pushNotificationTitle: '',
        pushNotificationBody: '',
        completedTitle: '',
        completedBody: '',
    }));
}

// Province codes selectable for an app target, mirroring the backend rule
// (QC → Quebec only, CA → the non-Quebec provinces).
function isProvinceAllowedForAppTarget(
    provinceCode: string,
    appTarget: string
): boolean {
    if (appTarget === 'QC') {
        return provinceCode === 'QC';
    }
    if (appTarget === 'CA') {
        return provinceCode !== 'QC';
    }
    return true;
}

// Prefills the reward form from a club's single tier reward, keeping a slot for
// every language even when the reward has copy for only some.
function tierRewardFromClub(club: LoyaltyClub): TierRewardFormValues {
    const reward = club.tierRewards?.[0];
    const translations = emptyTierRewardTranslations().map((empty) => {
        const found = reward?.translations?.find(
            (t) => t.language === empty.language
        );
        return found
            ? {
                  language: empty.language,
                  title: found.title,
                  description: found.description ?? '',
                  image: found.image ?? '',
                  pushNotificationTitle: found.pushNotificationTitle ?? '',
                  pushNotificationBody: found.pushNotificationBody ?? '',
                  completedTitle: found.completedTitle ?? '',
                  completedBody: found.completedBody ?? '',
              }
            : empty;
    });
    return {
        promotionId: reward?.promotionId ?? null,
        requiredPurchases: reward?.requiredPurchases ?? null,
        grantValidityDays: reward?.grantValidityDays ?? null,
        maxClaims: reward?.maxClaims ?? null,
        translations,
    };
}

function buildTierRewardPayload(
    tr: TierRewardFormValues
): LoyaltyClubTierRewardInput {
    return {
        promotionId: tr.promotionId as number,
        requiredPurchases: tr.requiredPurchases as number,
        grantValidityDays: tr.grantValidityDays ?? null,
        maxClaims: tr.maxClaims ?? null,
        translations: tr.translations.map((t) => ({
            language: t.language,
            title: t.title,
            description: t.description,
            image: t.image,
            pushNotificationTitle: t.pushNotificationTitle,
            pushNotificationBody: t.pushNotificationBody,
            completedTitle: t.completedTitle,
            completedBody: t.completedBody,
        })),
    };
}

function getChangedScheduleIds(
    current: Record<number, TierSchedule>,
    original: Record<number, TierSchedule>
): number[] {
    return Object.keys(current)
        .map(Number)
        .filter((id) => {
            const c = current[id];
            const o = original[id];
            return !o || c.startDate !== o.startDate || c.endDate !== o.endDate;
        });
}

// Config fields are editable when creating a club.
function CreateConfigSection({
    formik,
    programs,
    isFetchingPrograms,
}: {
    formik: FormikProps<ClubFormValues>;
    programs: LoyaltyProgram[];
    isFetchingPrograms: boolean;
}) {
    const gamesStore = useGamesStore();

    return (
        <Section title="Config">
            <div className={style.config_grid}>
                <LabeledField label="Name*">
                    <Input
                        placeholder="Internal club name"
                        value={formik.values.name}
                        onChange={(e) =>
                            formik.setFieldValue('name', e.target.value)
                        }
                    />
                </LabeledField>
                <LabeledField label="Loyalty Program*">
                    <Select<number>
                        placeholder="Select a program"
                        value={formik.values.loyaltyProgramId ?? undefined}
                        loading={isFetchingPrograms}
                        onChange={(value) =>
                            formik.setFieldValue('loyaltyProgramId', value)
                        }
                        options={programs.map((p) => ({
                            value: p.id,
                            label: p.name,
                        }))}
                    />
                </LabeledField>
                <LabeledField label="App Target*">
                    <Select<string>
                        value={formik.values.appTarget}
                        onChange={(value) => {
                            formik.setFieldValue('appTarget', value);
                            // The new target may no longer allow the picked
                            // game; drop it so the API does not reject the save.
                            const linked = gamesStore.gamesForSelect.find(
                                (game) =>
                                    game.id === formik.values.externalGameId
                            );
                            if (
                                linked &&
                                !isGameLinkableToLoyaltyClub(linked, value)
                            ) {
                                formik.setFieldValue('externalGameId', null);
                            }
                        }}
                        options={APP_TARGETS.map((t) => ({
                            value: t,
                            label: APP_TARGET_CONFIG[t]?.label ?? t,
                        }))}
                    />
                </LabeledField>
                <LabeledField label="External Provider">
                    <Select<ExternalProviderEnum | undefined>
                        placeholder="None"
                        allowClear
                        value={formik.values.externalProvider ?? undefined}
                        onChange={(value) =>
                            formik.setFieldValue(
                                'externalProvider',
                                value ?? null
                            )
                        }
                        options={PROVIDER_OPTIONS.map((p) => ({
                            value: p,
                            label: p,
                        }))}
                    />
                </LabeledField>
                <LabeledField label="Slug*">
                    <Input
                        placeholder="e.g. summer-club"
                        value={formik.values.slug}
                        onChange={(e) =>
                            formik.setFieldValue('slug', e.target.value)
                        }
                    />
                </LabeledField>
            </div>
        </Section>
    );
}

// Config is read-only when editing an existing club.
function EditConfigSection({
    programName,
    appTarget,
    externalProvider,
}: {
    programName: string;
    appTarget: string | null;
    externalProvider: string | null;
}) {
    return (
        <Section title="Config">
            <div className={style.config_grid}>
                <LabeledField label="Loyalty Program">
                    <Input value={programName} disabled />
                </LabeledField>
                <LabeledField label="App Target">
                    <Input
                        value={
                            appTarget
                                ? APP_TARGET_CONFIG[appTarget]?.label ??
                                  appTarget
                                : 'All apps'
                        }
                        disabled
                    />
                </LabeledField>
                {externalProvider && (
                    <LabeledField label="External Provider">
                        <div className={style.tag_field}>
                            <Tag variant="blue">{externalProvider}</Tag>
                        </div>
                    </LabeledField>
                )}
            </div>
        </Section>
    );
}

export const LoyaltyClubPage = observer(() => {
    const params = useParams<Params>();
    const isCreate = params.action === 'create';
    const clubId = Number(params.id!);
    const loyaltyClubStore = useLoyaltyClubStore();
    const promotionStore = usePromotionStore();
    const businessUnitStore = useBusinessUnitStore();
    const gamesStore = useGamesStore();
    const navigation = useNavigation();
    // Null on edit until the club loads, so other types skip the games fetch.
    const [clubType, setClubType] = React.useState<string | null>(
        isCreate ? REPEATABLE_PURCHASE_GRANT : null
    );
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isUpserting, setIsUpserting] = React.useState<boolean>(false);
    const [externalProvider, setExternalProvider] = React.useState<
        LoyaltyClub['externalProvider']
    >(null);
    const [loyaltyProgramId, setLoyaltyProgramId] = React.useState<
        number | null
    >(null);
    const [appTarget, setAppTarget] = React.useState<string | null>(null);

    const [tierRewardSchedules, setTierRewardScheduled] = React.useState<
        Record<number, TierSchedule>
    >({});
    const tierRewardSchedulesRef = React.useRef(tierRewardSchedules);
    tierRewardSchedulesRef.current = tierRewardSchedules;
    const originalTierRewardScheduledRef = React.useRef<
        Record<number, TierSchedule>
    >({});
    const tierRewardSchedulesDirty =
        getChangedScheduleIds(
            tierRewardSchedules,
            originalTierRewardScheduledRef.current
        ).length > 0;
    // Display names for the schedules above. Reusable coupons edit their own
    // dates per row, so only the other reward types appear here.
    const [scheduledTierRewards, setScheduledTierRewards] = React.useState<
        { id: number; label: string }[]
    >([]);

    const handleScheduleChange = React.useCallback(
        (id: number, field: keyof TierSchedule, value: string | null) => {
            setTierRewardScheduled((prev) => ({
                ...prev,
                [id]: {
                    startDate: prev[id]?.startDate ?? null,
                    endDate: prev[id]?.endDate ?? null,
                    [field]: value,
                },
            }));
        },
        []
    );

    const [reusableCoupons, setReusableCoupons] = React.useState<
        ReusableCouponRow[]
    >([]);
    const reusableCouponsRef = React.useRef(reusableCoupons);
    reusableCouponsRef.current = reusableCoupons;
    const originalReusableCouponsRef = React.useRef<string>('[]');
    const reusableCouponsDirty =
        JSON.stringify(buildReusableCouponPayload(reusableCoupons)) !==
        originalReusableCouponsRef.current;

    // A successful picker request, not the list's size: a successful empty
    // response is a valid, empty check-set, and conflating it with "still
    // loading" kept Save permanently disabled for clubs whose visible
    // promotions list is empty. A failed request keeps the flag down, because
    // validating coupon rows against the empty store list would skip the
    // privacy and type rules and fail the coupon PUT after the club has
    // already saved.
    const [promotionsLoaded, setPromotionsLoaded] = React.useState(false);
    const reusableCouponPromotionsReady = promotionsLoaded;
    const reusableCouponPromotions: ReusableCouponPromotion[] = React.useMemo(
        () =>
            promotionStore.promotionsForSelect.map((p) => ({
                id: p.id,
                title: p.items[0]?.title ?? `Promotion #${p.id}`,
                type: p.type,
                isPrivate: p.isPrivate ?? null,
                thumbnail: p.items[0]?.thumbnail ?? null,
                temperatureActivationFloor:
                    p.temperatureActivationFloor ?? null,
                temperatureActivationCeiling:
                    p.temperatureActivationCeiling ?? null,
            })),
        [promotionStore.promotionsForSelect]
    );
    // One validation pass feeds both the Save button and the row messages, so
    // the two can never disagree about whether the list is savable.
    const reusableCouponErrors = React.useMemo(
        () =>
            validateReusableCoupons(reusableCoupons, reusableCouponPromotions),
        [reusableCoupons, reusableCouponPromotions]
    );

    const authStore = useAuthStore();
    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const updateClub = useCallback(
        async (values: ClubFormValues) => {
            const current = tierRewardSchedulesRef.current;
            const original = originalTierRewardScheduledRef.current;
            const changedIds = getChangedScheduleIds(current, original);

            // Reject invalid windows up front for instant feedback.
            for (const id of changedIds) {
                const c = current[id];
                if (
                    c.startDate &&
                    c.endDate &&
                    new Date(c.startDate) > new Date(c.endDate)
                ) {
                    NotificationManager.showError(
                        'Coupon schedule is invalid: the start date must be on or before the end date.'
                    );
                    throw new Error('Invalid coupon schedule window');
                }
            }

            const couponRows = reusableCouponsRef.current;

            const payload: LoyaltyClubUpdatePayload = {
                name: values.name,
                isActive: values.isActive,
                startDate: values.startDate,
                registrationStartDate: values.registrationStartDate,
                registrationEndDate: values.registrationEndDate,
                endDate: values.endDate,
                six12PromotionId: values.six12PromotionId?.trim() || null,
                externalGameId: relatedGameIdForSave(
                    clubType,
                    values.externalGameId
                ),
                translations: values.translations,
                tierRewardSchedules: changedIds.map((id) => ({
                    id,
                    startDate: current[id].startDate,
                    endDate: current[id].endDate,
                })),
                provinces: values.provinces,
                // Only repeatablePurchaseGrant clubs carry an editable tier
                // reward; the backend rejects it for other types.
                ...(clubType === REPEATABLE_PURCHASE_GRANT
                    ? { tierReward: buildTierRewardPayload(values.tierReward) }
                    : {}),
            };

            setIsUpserting(true);
            try {
                await saveClubWithReusableCoupons(
                    couponRows,
                    originalReusableCouponsRef.current,
                    {
                        saveClub: () =>
                            loyaltyClubStore.updateLoyaltyClub(payload, clubId),
                        saveReusableCoupons: (coupons) =>
                            loyaltyClubStore.replaceReusableCoupons(
                                clubId,
                                coupons
                            ),
                        onClubSaved: () => {
                            originalTierRewardScheduledRef.current = JSON.parse(
                                JSON.stringify(current)
                            );
                        },
                        onReusableCouponsSaved: (
                            savedRows,
                            savedPayloadJson
                        ) => {
                            setReusableCoupons(savedRows);
                            originalReusableCouponsRef.current = savedPayloadJson;
                        },
                    },
                    reusableCouponPromotionsReady
                        ? reusableCouponPromotions
                        : null
                );
            } finally {
                setIsUpserting(false);
            }
        },
        [
            loyaltyClubStore,
            clubId,
            clubType,
            reusableCouponPromotions,
            reusableCouponPromotionsReady,
        ]
    );

    const createClub = useCallback(
        async (values: ClubFormValues) => {
            const payload: LoyaltyClubCreatePayload = {
                loyaltyProgramId: values.loyaltyProgramId!,
                name: values.name,
                slug: values.slug.trim().toLowerCase(),
                appTarget: values.appTarget as LoyaltyClubCreatePayload['appTarget'],
                externalProvider: values.externalProvider ?? null,
                six12PromotionId: values.six12PromotionId?.trim() || null,
                externalGameId: relatedGameIdForSave(
                    clubType,
                    values.externalGameId
                ),
                isActive: values.isActive,
                startDate: values.startDate,
                registrationStartDate: values.registrationStartDate,
                registrationEndDate: values.registrationEndDate,
                endDate: values.endDate,
                translations: values.translations,
                provinces: values.provinces,
                tierReward: buildTierRewardPayload(values.tierReward),
            };

            setIsUpserting(true);
            try {
                const created = await loyaltyClubStore.createLoyaltyClub(
                    payload
                );
                NotificationManager.showSuccess('Loyalty club created');
                return created;
            } finally {
                setIsUpserting(false);
            }
        },
        [loyaltyClubStore, clubType]
    );

    const formik = useFormik<ClubFormValues>({
        initialValues: {
            name: '',
            isActive: false,
            startDate: '',
            registrationStartDate: '',
            registrationEndDate: '',
            endDate: '',
            translations: Lang.map((language) => ({
                language,
                name: '',
                subtitle: null,
                description: null,
                image: null,
            })),
            loyaltyProgramId: null,
            appTarget: 'QC',
            externalProvider: null,
            six12PromotionId: null,
            externalGameId: null,
            slug: '',
            provinces: [],
            tierReward: {
                promotionId: null,
                requiredPurchases: null,
                grantValidityDays: null,
                maxClaims: null,
                translations: emptyTierRewardTranslations(),
            },
        },
        validateOnMount: true,
        validationSchema: isCreate
            ? YupLoyaltyClubCreatePayload
            : YupLoyaltyClubUpdatePayload,
        onSubmit: isCreate ? createClub : updateClub,
    });

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty || tierRewardSchedulesDirty || reusableCouponsDirty,
        onSave: async () => {
            if (isCreate) {
                await createClub(formik.values);
            } else {
                await updateClub(formik.values);
            }
        },
    });

    const formikValuesRef = React.useRef(formik.values);
    formikValuesRef.current = formik.values;

    const onSaveEdit = useCallback(async () => {
        try {
            await updateClub(formikValuesRef.current);
            clearDirty();
        } catch {
            // error notification already shown by store
        }
    }, [updateClub, clearDirty]);

    const onSaveCreate = useCallback(async () => {
        try {
            const created = await createClub(formikValuesRef.current);
            clearDirty(() => navigation.replaceToLoyaltyClubEdit(created.id));
        } catch {
            // error notification already shown by store
        }
    }, [createClub, clearDirty, navigation]);

    if (!YupParams.isValidSync(params)) {
        navigation.replaceToLoyaltyClubs();
        return null;
    }

    React.useEffect(() => {
        loyaltyClubStore.fetchLoyaltyPrograms();
        promotionStore.fetchForSelect().then((loaded) => {
            if (loaded) {
                setPromotionsLoaded(true);
            }
        });
        businessUnitStore.fetchBusinessUnits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showExternalGamePicker = canLinkRelatedGame(clubType);
    React.useEffect(() => {
        if (showExternalGamePicker) {
            gamesStore.fetchForSelect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showExternalGamePicker]);

    React.useEffect(() => {
        if (isCreate) {
            return;
        }
        setIsFetching(true);
        loyaltyClubStore
            .fetchLoyaltyClub(clubId)
            .then((club) => {
                setExternalProvider(club.externalProvider ?? null);
                setLoyaltyProgramId(club.loyaltyProgramId);
                setAppTarget(club.appTarget ?? null);
                setClubType(club.type);
                const rewards = club.tierRewards ?? [];

                // Reusable coupons save through their own endpoint, so they
                // stay out of the schedules the club update sends.
                const initialTierRewardScheduled: Record<
                    number,
                    TierSchedule
                > = {};
                rewards
                    .filter((r) => r.rewardType !== REUSABLE_COUPON_REWARD_TYPE)
                    .forEach((r) => {
                        initialTierRewardScheduled[r.id] = {
                            startDate: r.startDate ?? null,
                            endDate: r.endDate ?? null,
                        };
                    });
                setScheduledTierRewards(
                    rewards
                        .filter(
                            (r) => r.rewardType !== REUSABLE_COUPON_REWARD_TYPE
                        )
                        .map((r) => ({
                            id: r.id,
                            label:
                                r.promotionName ??
                                (r.promotionId != null
                                    ? `Promotion #${r.promotionId}`
                                    : `Reward #${r.id}`),
                        }))
                );
                setTierRewardScheduled(initialTierRewardScheduled);
                originalTierRewardScheduledRef.current = JSON.parse(
                    JSON.stringify(initialTierRewardScheduled)
                );

                const couponRows = reusableCouponRowsFromTierRewards(rewards);
                setReusableCoupons(couponRows);
                originalReusableCouponsRef.current = JSON.stringify(
                    buildReusableCouponPayload(couponRows)
                );
                formik.resetForm({
                    values: {
                        ...formik.values,
                        name: club.name,
                        isActive: club.active,
                        startDate: club.startDate,
                        registrationStartDate: club.registrationStartDate,
                        registrationEndDate: club.registrationEndDate,
                        endDate: club.endDate,
                        six12PromotionId: club.six12PromotionId ?? null,
                        externalGameId: club.externalGameId ?? null,
                        slug: club.slug ?? '',
                        translations: club.translations.map((t) => ({
                            language: t.language,
                            name: t.name,
                            subtitle: t.subtitle ?? null,
                            description: t.description ?? null,
                            image: t.image ?? null,
                        })),
                        provinces: (club.provinces ?? []).map(
                            (p) => p.provinceId
                        ),
                        tierReward: tierRewardFromClub(club),
                    },
                });
            })
            .catch(() => navigation.replaceToLoyaltyClubs())
            .finally(() => setIsFetching(false));
        // Keyed on clubId so the create→edit in-place navigation refetches
        // (the component stays mounted, so a mount-only effect would not).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clubId]);

    const effectiveAppTarget = isCreate
        ? formik.values.appTarget
        : appTarget ?? 'All';
    const externalGameOptions = React.useMemo(() => {
        if (!showExternalGamePicker) {
            return [];
        }
        return (
            gamesStore.gamesForSelect
                .filter((game) => game.type === 'external')
                // Mirrors the API's own link rule, which is NOT Featured's rule:
                // Featured's CA case demands the exact 12-province set, so asking
                // for QC and CA together is unsatisfiable. See
                // utils/loyalty-club-game-scope.
                .filter((game) =>
                    isGameLinkableToLoyaltyClub(game, effectiveAppTarget)
                )
                .map((game) => {
                    const title =
                        game.title.en || game.title.fr || `Game #${game.id}`;
                    const status = getGameAvailabilityStatus(game);
                    return {
                        value: game.id,
                        searchText: title,
                        label: (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                {game.image.en && (
                                    <img
                                        src={game.image.en}
                                        alt=""
                                        style={{
                                            width: 24,
                                            height: 24,
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <span style={{ flex: 1 }}>{title}</span>
                                <GameAvailabilityStatusTag status={status} />
                            </div>
                        ),
                    };
                })
        );
    }, [showExternalGamePicker, gamesStore.gamesForSelect, effectiveAppTarget]);

    // A club linked to a contest that has since ended, been deactivated, or
    // fallen out of province scope is not in /games/visible, so the Select
    // would render the stored id as a bare number. Keep it as an option,
    // labelled so it is obvious why it is no longer offered to others.
    const externalGameOptionsWithCurrent = React.useMemo(() => {
        const linkedId = formik.values.externalGameId;
        if (
            !showExternalGamePicker ||
            linkedId == null ||
            externalGameOptions.some((option) => option.value === linkedId)
        ) {
            return externalGameOptions;
        }
        return [
            ...externalGameOptions,
            {
                value: linkedId,
                searchText: `Game #${linkedId}`,
                label: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <span style={{ flex: 1 }}>{`Game #${linkedId}`}</span>
                        <GameAvailabilityStatusTag status="inactive" />
                    </div>
                ),
            },
        ];
    }, [
        showExternalGamePicker,
        externalGameOptions,
        formik.values.externalGameId,
    ]);

    const provinceOptions = (businessUnitStore.businessUnits ?? [])
        .flatMap((bu) => bu.provinces)
        .filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i)
        .filter((p) =>
            isProvinceAllowedForAppTarget(p.name, effectiveAppTarget)
        )
        .map((p) => ({ value: p.id, label: p.name }));
    const promotionOptions = promotionStore.promotionsForSelect.map((p) => ({
        value: p.id,
        label: p.items[0]?.title ?? `Promotion #${p.id}`,
    }));
    const hasReusableCouponErrors =
        Object.keys(reusableCouponErrors).length > 0;
    const reusableCouponsAwaitingPromotions =
        !isCreate &&
        reusableCoupons.length > 0 &&
        !reusableCouponPromotionsReady;
    const isRepeatable = clubType === REPEATABLE_PURCHASE_GRANT;

    return (
        <>
            <PageTopBar
                title={
                    isCreate ? 'New Loyalty Club' : `Loyalty Club #${params.id}`
                }
                goBack={{
                    callback: () => navigation.goToLoyaltyClubs(),
                    text: 'Back to list',
                }}
            >
                <SaveButton
                    onClick={isCreate ? onSaveCreate : onSaveEdit}
                    loading={isUpserting}
                    disabled={
                        !formik.isValid ||
                        hasReusableCouponErrors ||
                        reusableCouponsAwaitingPromotions
                    }
                />
            </PageTopBar>

            {isFetching ? (
                <Spin />
            ) : (
                <div className="content_with_margins">
                    <div className={style.promotion_page_content}>
                        <div className={style.left_side}>
                            {isCreate ? (
                                <CreateConfigSection
                                    formik={formik}
                                    programs={loyaltyClubStore.loyaltyPrograms}
                                    isFetchingPrograms={
                                        loyaltyClubStore.isFetchingLoyaltyPrograms
                                    }
                                />
                            ) : (
                                <EditConfigSection
                                    programName={
                                        loyaltyClubStore.loyaltyPrograms.find(
                                            (p) => p.id === loyaltyProgramId
                                        )?.name ??
                                        String(loyaltyProgramId ?? '—')
                                    }
                                    appTarget={appTarget}
                                    externalProvider={externalProvider ?? null}
                                />
                            )}

                            <Section title="Schedule">
                                <div className={style.schedule_grid}>
                                    <LabeledField label="Start Date">
                                        <DatePicker
                                            allowClear={false}
                                            showTime={false}
                                            format={DATE_FORMAT}
                                            value={
                                                formik.values.startDate || null
                                            }
                                            onChange={(date) =>
                                                formik.setFieldValue(
                                                    'startDate',
                                                    date
                                                        ? `${date.format(
                                                              DATE_FORMAT
                                                          )}T00:00:00.000Z`
                                                        : ''
                                                )
                                            }
                                        />
                                    </LabeledField>
                                    <LabeledField label="Registration Start Date">
                                        <DatePicker
                                            allowClear={false}
                                            showTime={false}
                                            format={DATE_FORMAT}
                                            value={
                                                formik.values
                                                    .registrationStartDate ||
                                                null
                                            }
                                            onChange={(date) =>
                                                formik.setFieldValue(
                                                    'registrationStartDate',
                                                    date
                                                        ? `${date.format(
                                                              DATE_FORMAT
                                                          )}T00:00:00.000Z`
                                                        : ''
                                                )
                                            }
                                        />
                                    </LabeledField>
                                    <LabeledField label="Registration End Date">
                                        <DatePicker
                                            allowClear={false}
                                            showTime={false}
                                            format={DATE_FORMAT}
                                            value={
                                                formik.values
                                                    .registrationEndDate || null
                                            }
                                            onChange={(date) =>
                                                formik.setFieldValue(
                                                    'registrationEndDate',
                                                    date
                                                        ? `${date.format(
                                                              DATE_FORMAT
                                                          )}T00:00:00.000Z`
                                                        : ''
                                                )
                                            }
                                        />
                                    </LabeledField>
                                    <LabeledField label="End Date">
                                        <DatePicker
                                            allowClear={false}
                                            showTime={false}
                                            format={DATE_FORMAT}
                                            value={
                                                formik.values.endDate || null
                                            }
                                            onChange={(date) =>
                                                formik.setFieldValue(
                                                    'endDate',
                                                    date
                                                        ? `${date.format(
                                                              DATE_FORMAT
                                                          )}T00:00:00.000Z`
                                                        : ''
                                                )
                                            }
                                        />
                                    </LabeledField>
                                </div>
                            </Section>

                            <Section title="Provinces">
                                <LabeledField label="Available in*">
                                    <Select<number[]>
                                        mode="multiple"
                                        style={{ width: '100%' }}
                                        placeholder="Select provinces"
                                        value={formik.values.provinces}
                                        loading={
                                            businessUnitStore.isFetchingBusinessUnits
                                        }
                                        onChange={(value) =>
                                            formik.setFieldValue(
                                                'provinces',
                                                value
                                            )
                                        }
                                        options={provinceOptions}
                                    />
                                </LabeledField>
                            </Section>

                            <Section title="Content">
                                <div className="columns_layout">
                                    {formik.values.translations.map(
                                        (item, i) => (
                                            <div
                                                className="column"
                                                key={item.language}
                                            >
                                                <div className="h5_bold">
                                                    {item.language === Lang[0]
                                                        ? 'English'
                                                        : 'French'}
                                                </div>
                                                <LabeledField
                                                    label={`Name (${item.language.toUpperCase()})*`}
                                                >
                                                    <Input
                                                        name={`translations[${i}].name`}
                                                        value={item.name}
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />
                                                </LabeledField>
                                                <LabeledField
                                                    label={`Subtitle (${item.language.toUpperCase()})`}
                                                >
                                                    <Input
                                                        name={`translations[${i}].subtitle`}
                                                        value={
                                                            item.subtitle ?? ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />
                                                </LabeledField>
                                                <LabeledField
                                                    label={`Description (${item.language.toUpperCase()})`}
                                                >
                                                    <Input.TextArea
                                                        name={`translations[${i}].description`}
                                                        value={
                                                            item.description ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        rows={3}
                                                    />
                                                </LabeledField>
                                                <LabeledField
                                                    label={`Image (${item.language.toUpperCase()})`}
                                                >
                                                    <ImageInput
                                                        url={item.image}
                                                        onChange={(url) =>
                                                            formik.setFieldValue(
                                                                `translations[${i}].image`,
                                                                url
                                                            )
                                                        }
                                                        onDelete={() =>
                                                            formik.setFieldValue(
                                                                `translations[${i}].image`,
                                                                null
                                                            )
                                                        }
                                                    />
                                                </LabeledField>
                                            </div>
                                        )
                                    )}
                                </div>
                            </Section>

                            {isRepeatable && (
                                <Section title="Reward">
                                    <div className={style.config_grid}>
                                        <LabeledField label="Promotion*">
                                            <Select<number>
                                                showSearch
                                                optionFilterProp="label"
                                                placeholder="Select a promotion"
                                                value={
                                                    formik.values.tierReward
                                                        .promotionId ??
                                                    undefined
                                                }
                                                loading={
                                                    promotionStore.isFetchingForSelect
                                                }
                                                onChange={(value) =>
                                                    formik.setFieldValue(
                                                        'tierReward.promotionId',
                                                        value
                                                    )
                                                }
                                                options={promotionOptions}
                                            />
                                        </LabeledField>
                                        <LabeledField label="Purchases required*">
                                            <InputNumber
                                                min={1}
                                                style={{ width: '100%' }}
                                                value={
                                                    formik.values.tierReward
                                                        .requiredPurchases ??
                                                    undefined
                                                }
                                                onChange={(v) =>
                                                    formik.setFieldValue(
                                                        'tierReward.requiredPurchases',
                                                        v ?? null
                                                    )
                                                }
                                            />
                                        </LabeledField>
                                        <LabeledField label="Grant validity (days)">
                                            <InputNumber
                                                min={1}
                                                style={{ width: '100%' }}
                                                placeholder="No expiry"
                                                value={
                                                    formik.values.tierReward
                                                        .grantValidityDays ??
                                                    undefined
                                                }
                                                onChange={(v) =>
                                                    formik.setFieldValue(
                                                        'tierReward.grantValidityDays',
                                                        v ?? null
                                                    )
                                                }
                                            />
                                        </LabeledField>
                                        <LabeledField label="Max claims">
                                            <InputNumber
                                                min={1}
                                                style={{ width: '100%' }}
                                                placeholder="Unlimited"
                                                value={
                                                    formik.values.tierReward
                                                        .maxClaims ?? undefined
                                                }
                                                onChange={(v) =>
                                                    formik.setFieldValue(
                                                        'tierReward.maxClaims',
                                                        v ?? null
                                                    )
                                                }
                                            />
                                        </LabeledField>
                                        <LabeledField label="Six12 promotion ID">
                                            <Input
                                                placeholder="Set once Six12 assigns it"
                                                value={
                                                    formik.values
                                                        .six12PromotionId ??
                                                    undefined
                                                }
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        'six12PromotionId',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </LabeledField>
                                        {showExternalGamePicker && (
                                            <LabeledField
                                                label="Related game"
                                                helperText="The app decides per member whether the contest shows, from their app and province."
                                            >
                                                <Select<number>
                                                    allowClear
                                                    showSearch
                                                    filterOption={(
                                                        input,
                                                        option
                                                    ) =>
                                                        (
                                                            option?.searchText ??
                                                            ''
                                                        )
                                                            .toLowerCase()
                                                            .includes(
                                                                input.toLowerCase()
                                                            )
                                                    }
                                                    placeholder="Select an external game"
                                                    loading={
                                                        gamesStore.isFetchingForSelect
                                                    }
                                                    value={
                                                        formik.values
                                                            .externalGameId ??
                                                        undefined
                                                    }
                                                    onChange={(value) =>
                                                        formik.setFieldValue(
                                                            'externalGameId',
                                                            value ?? null
                                                        )
                                                    }
                                                    options={
                                                        externalGameOptionsWithCurrent
                                                    }
                                                />
                                            </LabeledField>
                                        )}
                                    </div>
                                    <div
                                        className="columns_layout"
                                        style={{ marginTop: 24 }}
                                    >
                                        {formik.values.tierReward.translations.map(
                                            (t, i) => (
                                                <div
                                                    className="column"
                                                    key={t.language}
                                                >
                                                    <div className="h5_bold">
                                                        {t.language === Lang[0]
                                                            ? 'English'
                                                            : 'French'}
                                                    </div>
                                                    <LabeledField
                                                        label={`Title (${t.language.toUpperCase()})*`}
                                                    >
                                                        <Input
                                                            name={`tierReward.translations[${i}].title`}
                                                            value={t.title}
                                                            onChange={
                                                                formik.handleChange
                                                            }
                                                        />
                                                    </LabeledField>
                                                    <LabeledField
                                                        label={`Description (${t.language.toUpperCase()})*`}
                                                    >
                                                        <Input.TextArea
                                                            name={`tierReward.translations[${i}].description`}
                                                            value={
                                                                t.description ??
                                                                ''
                                                            }
                                                            onChange={
                                                                formik.handleChange
                                                            }
                                                            rows={2}
                                                        />
                                                    </LabeledField>
                                                    <LabeledField
                                                        label={`Image (${t.language.toUpperCase()})*`}
                                                    >
                                                        <ImageInput
                                                            url={t.image}
                                                            onChange={(url) =>
                                                                formik.setFieldValue(
                                                                    `tierReward.translations[${i}].image`,
                                                                    url
                                                                )
                                                            }
                                                            onDelete={() =>
                                                                formik.setFieldValue(
                                                                    `tierReward.translations[${i}].image`,
                                                                    ''
                                                                )
                                                            }
                                                        />
                                                    </LabeledField>
                                                    <LabeledField
                                                        label={`Push title (${t.language.toUpperCase()})*`}
                                                    >
                                                        <Input
                                                            name={`tierReward.translations[${i}].pushNotificationTitle`}
                                                            value={
                                                                t.pushNotificationTitle ??
                                                                ''
                                                            }
                                                            onChange={
                                                                formik.handleChange
                                                            }
                                                        />
                                                    </LabeledField>
                                                    <LabeledField
                                                        label={`Push body (${t.language.toUpperCase()})*`}
                                                    >
                                                        <Input.TextArea
                                                            name={`tierReward.translations[${i}].pushNotificationBody`}
                                                            value={
                                                                t.pushNotificationBody ??
                                                                ''
                                                            }
                                                            onChange={
                                                                formik.handleChange
                                                            }
                                                            rows={2}
                                                        />
                                                    </LabeledField>
                                                    <LabeledField
                                                        label={`Completed title (${t.language.toUpperCase()})*`}
                                                    >
                                                        <Input
                                                            name={`tierReward.translations[${i}].completedTitle`}
                                                            value={
                                                                t.completedTitle ??
                                                                ''
                                                            }
                                                            onChange={
                                                                formik.handleChange
                                                            }
                                                        />
                                                    </LabeledField>
                                                    <LabeledField
                                                        label={`Completed body (${t.language.toUpperCase()})*`}
                                                    >
                                                        <Input.TextArea
                                                            name={`tierReward.translations[${i}].completedBody`}
                                                            value={
                                                                t.completedBody ??
                                                                ''
                                                            }
                                                            onChange={
                                                                formik.handleChange
                                                            }
                                                            rows={2}
                                                        />
                                                    </LabeledField>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </Section>
                            )}

                            {/* Needs a club id to save against, so edit only. */}
                            {!isCreate && (
                                <UnlimitedCoupons
                                    rows={reusableCoupons}
                                    promotions={reusableCouponPromotions}
                                    errors={reusableCouponErrors}
                                    isLoadingPromotions={
                                        promotionStore.isFetchingForSelect
                                    }
                                    onChange={setReusableCoupons}
                                />
                            )}

                            {/* Entry and coupon-choice rewards still date
                                through the club update; reusable coupons set
                                their dates per row instead. */}
                            {!isCreate && scheduledTierRewards.length > 0 && (
                                <Section title="Reward dates">
                                    {scheduledTierRewards.map((reward) => {
                                        const schedule = tierRewardSchedules[
                                            reward.id
                                        ] ?? {
                                            startDate: null,
                                            endDate: null,
                                        };
                                        return (
                                            <div
                                                key={reward.id}
                                                className={
                                                    style.reward_dates_row
                                                }
                                            >
                                                <span>{reward.label}</span>
                                                <LabeledField label="Start date">
                                                    <DatePicker
                                                        showTime={false}
                                                        format={DATE_FORMAT}
                                                        value={
                                                            schedule.startDate
                                                        }
                                                        onChange={(date) =>
                                                            handleScheduleChange(
                                                                reward.id,
                                                                'startDate',
                                                                date
                                                                    ? date.format(
                                                                          DATE_FORMAT
                                                                      )
                                                                    : null
                                                            )
                                                        }
                                                    />
                                                </LabeledField>
                                                <LabeledField label="End date">
                                                    <DatePicker
                                                        showTime={false}
                                                        format={DATE_FORMAT}
                                                        value={schedule.endDate}
                                                        onChange={(date) =>
                                                            handleScheduleChange(
                                                                reward.id,
                                                                'endDate',
                                                                date
                                                                    ? date.format(
                                                                          DATE_FORMAT
                                                                      )
                                                                    : null
                                                            )
                                                        }
                                                    />
                                                </LabeledField>
                                            </div>
                                        );
                                    })}
                                </Section>
                            )}
                        </div>

                        <div className={style.right_side}>
                            <Section title="Status">
                                <div className={style.status_column}>
                                    <div className={style.label_and_toggle}>
                                        <label className="footnote_description_bold">
                                            Active
                                        </label>
                                        <Switch
                                            checked={formik.values.isActive}
                                            onChange={(checked) =>
                                                formik.setFieldValue(
                                                    'isActive',
                                                    checked
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </Section>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});
